/**
 * Cliente HTTP del in-app billing, compartido por las apps de la familia. Pega
 * contra `/api/iap/*`:
 *   - `GET /api/iap/packs` → catálogo de packs (SKU → créditos), fuente de
 *     verdad del backend.
 *   - `POST /api/iap/sync` → tras una compra: el backend consulta RevenueCat,
 *     acredita lo pendiente (idempotente) y devuelve el balance fresco.
 *
 * La compra en sí (RevenueCat + store nativa) NO vive acá — es per-app y
 * nativa (hoy sólo speak, `apps/speak/src/billing/revenuecat.ts`). Las apps
 * web-only pueden listar los packs igual (catálogo informativo).
 *
 * Montos en nano-USD como string (BigInt-safe); `*Usd` ya vienen formateados.
 */
import { apiFetch } from '@orborix/auth-client';

export interface CreditPackDto {
  /** SKU = productId en Play/App Store + RevenueCat. */
  productId: string;
  /** Créditos que acredita el pack (nano-USD, string BigInt-safe). */
  creditsNano: string;
  /** Créditos formateados USD (ej. "9.99"). */
  creditsUsd: string;
  /** Precio de referencia USD (la store cobra el precio localizado real). */
  priceUsd: string;
}

export interface IapSyncResult {
  /** Cuántas compras nuevas se acreditaron en este sync. */
  creditedCount: number;
  balanceNano: string;
  balanceUsd: string;
}

export class IapApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'IapApiError';
    this.status = status;
    this.code = code;
  }
}

async function asError(res: Response): Promise<IapApiError> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return new IapApiError(body.error ?? `HTTP ${res.status}`, res.status, body.error ?? 'unknown');
}

export async function fetchCreditPacks(signal?: AbortSignal): Promise<CreditPackDto[]> {
  let res: Response;
  try {
    res = await apiFetch('/api/iap/packs', signal ? { signal } : {});
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new IapApiError('No pudimos conectar con el servidor.', 0, 'network_error');
  }
  if (!res.ok) throw await asError(res);
  const json = (await res.json()) as { packs: CreditPackDto[] };
  return json.packs;
}

export async function syncIapPurchases(): Promise<IapSyncResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/iap/sync', { method: 'POST' });
  } catch {
    throw new IapApiError('No pudimos conectar con el servidor.', 0, 'network_error');
  }
  if (!res.ok) throw await asError(res);
  return (await res.json()) as IapSyncResult;
}
